import { getCurrencySymbol } from "./finance";

/**
 * Format a number with locale-specific thousand separators.
 * Uses 'en-US' locale by default for comma separators (e.g., 1,234.56).
 *
 * @param value - The number to format
 * @param options - Intl.NumberFormatOptions for customization
 * @param locale - Locale string (default: 'en-US' for comma separators)
 */
export function formatNumber(
	value: number,
	options?: Intl.NumberFormatOptions,
	locale: string = "en-US",
): string {
	return Number(value).toLocaleString(locale, options);
}

/**
 * Format a number using compact notation (e.g., 10K, 1M).
 * Useful for chart axes to save space without cluttering the UI with currency symbols.
 */
export function formatCompactNumber(
	value: number,
	locale: string = "en-US", // Defaulting to en-US ensures we get 'K' and 'M' rather than 'mil'.
): string {
	if (value === 0) return "0";
	return new Intl.NumberFormat(locale, {
		notation: "compact",
		compactDisplay: "short",
		maximumFractionDigits: 1,
	}).format(value);
}

/**
 * Format a currency amount with symbol and consistent formatting.
 * Always uses comma separators for amounts > 999.
 *
 * @param amount - The amount to format
 * @param currency - Currency code (PEN, USD, EUR)
 * @param options - Optional formatting options
 */
export function formatCurrency(
	amount: number,
	currency: string = "PEN",
	options?: {
		minimumFractionDigits?: number;
		maximumFractionDigits?: number;
		showSymbol?: boolean;
	},
): string {
	const {
		minimumFractionDigits = 2,
		maximumFractionDigits = 2,
		showSymbol = true,
	} = options || {};

	const formattedNumber = formatNumber(amount, {
		minimumFractionDigits,
		maximumFractionDigits,
	});

	if (showSymbol) {
		return `${getCurrencySymbol(currency)} ${formattedNumber}`;
	}

	return formattedNumber;
}

/**
 * The date format every table uses: "10 jul. 2026". Shared so a date never
 * renders one way in one table and another way in the next.
 */
export const SHORT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
	day: "2-digit",
	month: "short",
	year: "numeric",
};

/** Same, without the day — for recurring rows shown as month ranges. */
export const MONTH_YEAR_OPTIONS: Intl.DateTimeFormatOptions = {
	month: "short",
	year: "numeric",
};

/**
 * Format a date string for display.
 *
 * @param value - Date string or Date object
 * @param locale - Locale for formatting (default: browser locale)
 * @param options - Intl.DateTimeFormatOptions for customization
 */
export function formatDate(
	value: string | number | Date | null | undefined,
	locale?: string,
	options?: Intl.DateTimeFormatOptions,
): string {
	if (!value) return "—";

	let date: Date;

	// Fix: If it's a string looking like YYYY-MM-DD (length 10), parse as local
	if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
		// Appending T00:00:00 forces local time parsing in modern browsers
		date = new Date(value + "T00:00:00");
	} else {
		date = value instanceof Date ? value : new Date(value);
	}

	// Handle invalid dates
	if (isNaN(date.getTime())) return "—";

	const resolvedLocale = locale === "es" ? "es-PE" : locale || "en-US";
	return date.toLocaleDateString(resolvedLocale, options);
}

/**
 * Formats a date to show only month and year (curated for charts/tables).
 * @param date - The date to format
 * @param language - The language to use
 * @returns Formatted date string (e.g., "Ene 2024" or "Jan 2024")
 */
export const formatMonthYear = (
	date: string | number | Date,
	language: string = "es",
): string => {
	return formatDate(date, language, {
		month: "short",
		year: "numeric",
	});
};

/**
 * Format a date as YYYY-MM-DD (ISO format without time).
 * Useful for date inputs and API payloads.
 */
export function formatDateISO(value: string | Date): string {
	const date = typeof value === "string" ? new Date(value) : value;
	// Use local time components to avoid UTC shift
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
}

/**
 * Calculates the age in years and months from a given start date up to today.
 *
 * @param date - The start date (e.g. delivery date or acquisition date)
 * @returns An object with years and months, or null if the date is invalid or in the future
 */
export function calculateAge(
	date: string | Date | null | undefined,
): { years: number; months: number } | null {
	if (!date) return null;

	const startDate = typeof date === "string" ? new Date(date) : date;
	const today = new Date();

	// If date is invalid or in the future
	if (isNaN(startDate.getTime()) || startDate > today) {
		return null;
	}

	let years = today.getFullYear() - startDate.getFullYear();
	let months = today.getMonth() - startDate.getMonth();

	if (months < 0 || (months === 0 && today.getDate() < startDate.getDate())) {
		years--;
		months += 12;
	}

	// Adjust months if negative after year decrement
	if (today.getDate() < startDate.getDate()) {
		months--;
		if (months < 0) {
			months += 12;
			// No additional year decrement needed here, logic above handles month overflow
		}
	}

	return { years: Math.max(0, years), months: Math.max(0, months) };
}

/**
 * Format period end date from provider/subscription data.
 * Handles BOTH formats:
 * - Legacy provider date: 'dd-MM-yyyy HH:mm:ss' (e.g., '25-02-2026 00:00:00')
 * - Database: 'yyyy-MM-dd HH:mm:ss' or ISO format (e.g., '2026-02-25 05:00:00+00')
 */
export function formatPeriodEnd(
	dateStr: string | null | undefined,
	locale: string = "es",
): string {
	if (!dateStr) {
		return locale === "es" ? "No disponible" : "N/A";
	}

	try {
		// Remove timezone info if present
		const cleanDateStr = dateStr.split("+")[0].trim();
		const datePart = cleanDateStr.split(" ")[0]; // Get date part only
		const parts = datePart.split("-");

		if (parts.length === 3) {
			// Determine format by checking which part is likely the year (4 digits)
			const firstPart = parseInt(parts[0], 10);
			const secondPart = parseInt(parts[1], 10);
			const thirdPart = parseInt(parts[2], 10);

			let day: number, month: number, year: number;

			// If first part is 4 digits, it's ISO format (yyyy-MM-dd)
			if (firstPart > 1000) {
				year = firstPart;
				month = secondPart - 1; // Month is 0-indexed
				day = thirdPart;
			}
			// Otherwise it's provider format (dd-MM-yyyy)
			else {
				day = firstPart;
				month = secondPart - 1; // Month is 0-indexed
				year = thirdPart;
			}

			// Create date in local timezone to avoid offset issues
			const date = new Date(year, month, day);

			if (!isNaN(date.getTime())) {
				// Use the existing formatDate function which uses Intl under the hood
				return formatDate(date, locale);
			}
		}

		// Final fallback: try parsing as ISO string
		const date = new Date(dateStr);
		if (!isNaN(date.getTime())) {
			return formatDate(date, locale);
		}

		return locale === "es" ? "No disponible" : "N/A";
	} catch (e) {
		console.error("Period end formatting error:", e, "Input:", dateStr);
		return locale === "es" ? "No disponible" : "N/A";
	}
}
